'use client'
import { useMemo } from 'react'

import type { CodeSnippetProps } from '@graftcode/design-system'
import { CodeSnippet, Icon, IconComponent } from '@graftcode/design-system'

import { getLanguageForPM } from '@common/core/utils/technology'

import { NoTechnologyPlaceholder } from '@/core/components/Card/components/NoTechnologyPlaceholder'
import { MethodParameters } from '@/core/components/MethodParameters'
import { MethodReturns } from '@/core/components/MethodReturns'
import { MethodSignature } from '@/core/components/MethodSignature'
import { Runtime } from '@/core/enums/runtime.enum'
import { useMethodExecution } from '@/core/hooks'
import type { ClassData, MethodData } from '@/core/types/library.type'

import styles from './MethodCardContent.module.scss'

interface MethodCardContentProps {
  methodKey: string
  method: MethodData
  classData: ClassData
  runtimeName: Runtime
}

export function MethodCardContent({ method, methodKey, classData, runtimeName }: MethodCardContentProps) {
  const {
    code,
    executionLogs,
    valueMap,
    variableCode,
    selectedTechnology,
    snippetTechnology,
    constructors,
    shouldShowConstructorSelector,
    selectedConstructorId,
    constructorValueMap,
    handleValueChange,
    handleRun,
    handleClear,
    handleTryIt,
    onConstructorChange,
    onConstructorValueChange,
  } = useMethodExecution({ method, methodKey, classData, runtimeName })

  const language = useMemo(
    (): string | undefined => (snippetTechnology ? getLanguageForPM(snippetTechnology) : undefined),
    [snippetTechnology]
  )

  const snippetKey = useMemo(
    () => `method-${methodKey}-${selectedTechnology || 'none'}-${snippetTechnology || 'none'}`,
    [methodKey, selectedTechnology, snippetTechnology]
  )

  return (
    <div className={styles.method}>
      <MethodSignature
        name={method.name}
        returns={method.returns}
        returnTypeUsage={method.returnTypeUsage}
        parameters={method.parameters}
      />

      <div className={styles.parametersReturnsSection}>
        {method?.parameters?.length > 0 && (
          <>
            <MethodParameters parameters={method.parameters} />
            <IconComponent size={16} icon={Icon.ArrowRightRegular} />
          </>
        )}
        <div className={styles.returnsSection}>
          <MethodReturns returns={method.returns} returnTypeUsage={method.returnTypeUsage} />
        </div>
      </div>

      <div className={styles.exampleSection}>
        <CodeSnippet
          key={snippetKey}
          {...({
            className: styles.exampleContent,
            code,
            contentVariant: 'advanced',
            isExampleMode: true,
            language,
            noTechnologyPlaceholder: <NoTechnologyPlaceholder />,
            exampleParameters: method.parameters,
            valueMap,
            variableCode,
            logs: executionLogs,
            onExampleRun: handleRun,
            onExampleClear: handleClear,
            onExampleValueChange: handleValueChange,
            onTryItToggle: handleTryIt,
            ...(shouldShowConstructorSelector && constructors
              ? {
                  constructors,
                  selectedConstructorId,
                  constructorValueMap,
                  onConstructorChange,
                  onConstructorValueChange,
                }
              : {}),
          } as CodeSnippetProps & { onTryItToggle?: () => void })}
        />
      </div>
    </div>
  )
}
